import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Adults face multiple barriers in pursuing education opportunities",
  "lead": "Analysing barriers to adult education and learning requires a clear framework.",
  "layout": "focus",
  "article": "access",
  "year": 2020
};
function _createMdxContent(props) {
  const _components = {
    h1: "h1",
    h2: "h2",
    h4: "h4",
    hr: "hr",
    p: "p",
    ..._provideComponents(),
    ...props.components
  }, {Aside, BackLink, FocusDetails, FocusImage, FocusThemeList, Header, ReferenceButton, Share} = _components;
  if (!Aside) _missingMdxReference("Aside", true);
  if (!BackLink) _missingMdxReference("BackLink", true);
  if (!FocusDetails) _missingMdxReference("FocusDetails", true);
  if (!FocusImage) _missingMdxReference("FocusImage", true);
  if (!FocusThemeList) _missingMdxReference("FocusThemeList", true);
  if (!Header) _missingMdxReference("Header", true);
  if (!ReferenceButton) _missingMdxReference("ReferenceButton", true);
  if (!Share) _missingMdxReference("Share", true);
  return _jsxs(_Fragment, {
    children: [_jsxs(Header, {
      children: [_jsx(BackLink, {
        name: "focusPosts",
        children: "All focuses"
      }), _jsx(Share, {
        message: "Adults face multiple barriers in pursuing education opportunities",
        disableEmbed: true
      })]
    }), "\n", _jsxs(FocusDetails, {
      url: "https://unesdoc.unesco.org/ark:/48223/pf0000373718/PDF/373718eng.pdf.multi.page=261",
      children: [_jsx(_components.h1, {
        id: "adults-face-multiple-barriers-in-pursuing-education-opportunities",
        children: "Adults face multiple barriers in pursuing education opportunities"
      }), _jsx(_components.p, {
        children: "Analysing barriers to adult education and learning requires a clear framework. The PIAAC survey refers to barriers as factors preventing adults from participating in formal or non-formal education. Only non-participants in education are asked these questions; those already participating are not asked what prevented them from further increasing their participation. By contrast, the AES distinguishes between those not interested in education or training and those willing to participate, and investigates barriers to participation among the latter."
      }), _jsx(_components.p, {
        children: "How barriers are categorized matters. A long-standing categorization (Cross, 1981) describes factors preventing participation as situational (e.g. life circumstances, such as family responsibilities or lack of time), dispositional (e.g. determined by previous learning experiences and personal disposition towards learning) and institutional (e.g. structural conditions hampering access, such as cost, lack of support, rigid schedules or limited provision) (UIL, 2019)."
      }), _jsx(_components.p, {
        children: "Dispositional barriers are generally less investigated in surveys and thus underestimated (Rubenson, 2011). Yet, when measured, they are the strongest factor hindering adult learning in most countries. On average, across EU countries, almost 60% of respondents do not participate in adult learning mainly because they see no need for it. Cost and inconvenient training schedules or locations are the most pressing institutional barriers. Lack of time and family responsibilities are the most common situational barriers, according to both PIAAC and AES data."
      }), _jsx(FocusImage, {
        size: "medium",
        src: "11-Adult_education_1.png",
        alt: "Negative previous learning experiences discourage many adults from participating in adult education",
        href: "https://unesdoc.unesco.org/ark:/48223/pf0000373718/PDF/373718eng.pdf.multi.page=262"
      }), _jsx(_components.p, {
        children: "Analysis of complementary PIAAC evidence for this report supports or extends these findings. Respondents in Greece and Türkiye are most affected by situational barriers. Lack of time was a recurring concern in Japan, the Republic of Korea and Singapore, where respondents reported being too busy at work. In terms of institutional barriers, training cost prevented between 25% and 30% of adults in Greece, Israel and Slovenia from participating; in Finland, around one in five cited inconvenient locations or schedules."
      }), _jsx(_components.p, {
        children: "France stands out in terms of lack of employer or public service support, with only slight improvement between the 2011 and 2016 AES rounds. This is despite the Compte Personnel de Formation (Personal Training Account), a programme introduced in 2014 that allows employees to convert accumulated time credits into grants, as well as training leave, part-time work or early retirement. Financial incentives only partly address the barriers. They need to be combined with non-financial instruments, such as counselling services and information awareness campaigns (OECD, 2019b)."
      }), _jsx(_components.p, {
        children: "While men were slightly more likely to mention scheduling as a barrier, women in all countries except Denmark were far more likely to mention family responsibilities. The tendency is higher in southern Europe, with up to two-thirds of female respondents in some countries unable to participate for this reason."
      }), _jsx(FocusImage, {
        size: "medium",
        src: "11-Adult_education_2.png",
        alt: "Women in European countries were almost twice as likely as men not to participate in adult education for family-related reasons",
        href: "https://unesdoc.unesco.org/ark:/48223/pf0000373718/PDF/373718eng.pdf.multi.page=263"
      }), _jsx(_components.p, {
        children: "A comparative study based on 14 time-use surveys and 5 household surveys in 19 countries found that men allocated slightly more time to learning, leisure and social activities. Albania, Ghana, Pakistan and the Republic of Moldova reported the highest gender imbalances: Ghanaian women spent almost two hours per day less than men on such activities (Rubiano-Matulevich and Viollaz, 2019). Women were more likely to see cost as an obstacle but less likely to have scheduling conflicts, probably reflecting their lower labour force participation and higher part-time employment rates."
      })]
    }), "\n", _jsxs(Aside, {
      mobilePlacement: "bottom",
      children: [_jsx(_components.h4, {
        id: "focuses-published-in-access",
        children: "Focuses published in Access"
      }), _jsx(_components.hr, {}), _jsx(FocusThemeList, {
        theme: "access"
      })]
    }), "\n", _jsx(_components.h2, {
      id: "references",
      children: "References"
    }), "\n", _jsx(_components.p, {
      children: "The full list of references can be found at this link."
    }), "\n", _jsx(ReferenceButton, {
      url: "https://unesdoc.unesco.org/ark:/48223/pf0000373718/PDF/373718eng.pdf.multi.page=505",
      children: _jsx(_components.p, {
        children: "2020 GEM Report"
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
